import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import cn from 'clsx';
import { debounce } from 'lodash';
import styles from './styles.module.scss';
import { BasicDropDown } from '@components-clean/core/basic-dropdown';
import CoreSplitter from '@components-clean/core/core-splitter';
import Loading from '@components-clean/core/loading/loading';
import { formatDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { ControlsHubPopoutChart } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/popout/chart';
import ControlsHubChartGrid from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/popout/chart.grid';
import { ControlsHubPopoutRisksAndObs } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/popout/tables/tables';
import { Permissions } from '@contexts/user-context';
import { useAuthorization, useTranslation } from '@hooks/index';
import Constants, { SplitterOrientation } from '@utils/constants';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { GET_CM2_PANEL } from '@queries/central-monitoring';
import { cm2Panel, cm2PanelVariables } from '@queries/generated/central-monitoring.generated';
import { controlsHubChartConfig } from '@queries/generated/risk-assessment.generated';
import { GET_CM_CONTROLS_HUB_CHART_CONFIG } from '@queries/risk-assessment';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { useGetRiskReviewsList } from '@apis/bff-api/report-templates';

const CmControlChartsPopout = () => {
    const user = useAuthorization();
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog.popOut' });
    const params = useParams();
    const studyId: string | undefined = params?.studyId ?? undefined;
    const chartId: string | undefined = params?.chartId ?? undefined;
    const { data: datasets, isLoading: isLoadingDatasets } = DatasetApi.useGetActive(
        studyId ?? '',
        studyId === undefined,
    );

    const { data: riskReviewStartDate, loading } = useQuery<controlsHubChartConfig>(GET_CM_CONTROLS_HUB_CHART_CONFIG, {
        variables: { studyId },
    });

    const { data: riskReviewData, isValidating: isValidatingRiskReviews } = useGetRiskReviewsList(studyId ?? '');

    const sortedDatasets = useMemo(() => {
        if (!datasets || isLoadingDatasets || loading || !riskReviewStartDate) return [];

        return datasets
            .map(formatDatasetDates)
            .sort(sortDatasetByDatesDesc)
            .filter(
                (dataset) =>
                    !riskReviewStartDate.controlsHubChartConfig?.cutoffDate ||
                    stringDateWithoutTimeFromUtc(dataset.dataCutDate!)! <=
                        stringDateWithoutTimeFromUtc(riskReviewStartDate.controlsHubChartConfig?.cutoffDate)!,
            );
    }, [datasets, isLoadingDatasets, loading, riskReviewStartDate]);

    const [selectedDataset, setSelectedDataset] = useState<CM2Dataset | undefined>();
    useEffect(() => {
        if (!datasets || isValidatingRiskReviews || loading || !riskReviewStartDate) return;
        setSelectedDataset(sortedDatasets.length > 0 ? sortedDatasets[0] : undefined);
    }, [datasets, riskReviewData, isValidatingRiskReviews, loading, sortedDatasets]);

    const canView = user?.can?.({
        module: Constants.Modules.RAM,
        page: Constants.PageElements.CMControls,
        permission: Permissions.Enabled,
        element: Constants.PageElements.CMControls,
    });

    const [panes, setPanes] = useState<SplitterPaneProps[]>([{ size: '40%', min: '15px' }, { min: '35px' }]);
    const onPaneChange = (event: SplitterOnChangeEvent) => {
        debounce(() => setPanes(event.newState), 20);
    };

    if (!canView) return null;

    const { data: panelData } = useQuery<cm2Panel, cm2PanelVariables>(GET_CM2_PANEL, {
        variables: { panelId: chartId, timestamp: null },
        skip: !chartId,
        fetchPolicy: 'no-cache',
    });

    if (!panelData || !selectedDataset) return <Loading />;

    return (
        <>
            <div
                className="k-card k-d-flex k-align-items-center k-p-2 k-bg-white k-mb-2"
                style={{ flexDirection: 'row' }}
            >
                <span className="k-flex-shrink-0" style={{ fontSize: '18px' }}>
                    {panelData?.cm2Panel?.plotTitle}
                </span>
                <div className={cn(styles.spacer, 'k-align-self-center')}>&nbsp;</div>
                <span className="k-flex-shrink-0 k-pl-2">{t('selectors.dataset')}</span>
                <div>
                    <BasicDropDown
                        monitoringToolkitTopbar
                        data-testid="ram-cm-controls-popout-dataset"
                        data={sortedDatasets}
                        dataItemKey="id"
                        textField="dataCutDate"
                        value={sortedDatasets.find((d) => d.id === selectedDataset?.id)}
                        onChange={async (e) => {
                            const dataset = e.value as CM2Dataset;
                            setSelectedDataset(dataset);
                        }}
                        disabled={!selectedDataset || isLoadingDatasets}
                        filterable
                        className="k-flex-shrink-0"
                        customClassName="basic-dropdown-styling"
                        allowClear={false}
                        cardHeader
                    />
                </div>
            </div>
            <div style={{ height: 'calc(100% - 52px)' }} className="">
                <CoreSplitter
                    className="!k-h-full"
                    orientation={SplitterOrientation.vertical}
                    data-testid="ram-cm-controls-popout-splitter"
                    alignmentValues={panes}
                    onChange={onPaneChange}
                >
                    <div className="k-h-full k-pb-2">
                        <div className="k-h-full k-card">
                            <ControlsHubChartGrid
                                studyId={studyId}
                                dataset={selectedDataset!}
                                chartId={chartId}
                                panelData={panelData}
                            />
                        </div>
                    </div>
                    <div className="k-d-flex k-gap-x-lg k-pt-2 !k-h-full !k-w-full">
                        <div
                            style={{ width: '532px', height: '367px', flexShrink: '0' }}
                            className={styles.popoutchart}
                        >
                            <ControlsHubPopoutChart
                                studyRef={studyId!}
                                dataset={selectedDataset!}
                                chartId={chartId!}
                                panelData={panelData}
                            />
                        </div>
                        <ControlsHubPopoutRisksAndObs
                            studyRef={studyId!}
                            dataset={selectedDataset!}
                            panelData={panelData}
                        />
                    </div>
                </CoreSplitter>
            </div>
        </>
    );
};
export default CmControlChartsPopout;
